import React from 'react';

const DataDeletion = () => {
  const styles = {
    page: { background: '#f8fafc', minHeight: '100vh', padding: '48px 20px', fontFamily: '"Inter", Arial, sans-serif' },
    card: { maxWidth: '820px', margin: '0 auto', background: '#ffffff', borderRadius: '16px', padding: '48px', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', color: '#1e293b', lineHeight: '1.7' },
    h1: { fontSize: '32px', fontWeight: '800', marginBottom: '8px', color: '#0f172a' },
    meta: { color: '#64748b', fontSize: '14px', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0' },
    h2: { fontSize: '20px', fontWeight: '700', marginTop: '36px', marginBottom: '12px', color: '#0f172a' },
    ol: { paddingLeft: '22px', margin: '12px 0' },
    ul: { paddingLeft: '22px', margin: '12px 0' },
    li: { marginBottom: '10px' },
    callout: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px 24px', marginTop: '12px' },
    highlight: { background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px 24px', marginTop: '12px' },
    link: { color: '#059669', fontWeight: '600', textDecoration: 'none' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Data Deletion Instructions</h1>
        <div style={styles.meta}>
          <strong>Tesla CRM Connect</strong>, a product of Tesla Online Solution<br />
          Effective date: 20 August 2026
        </div>

        <h2 style={styles.h2}>Your Right to Deletion</h2>
        <p>
          You can ask us to delete your data held in Tesla CRM Connect at any time, free of charge. This page explains
          exactly how to make that request and what happens after you do.
        </p>

        <h2 style={styles.h2}>How to Request Deletion</h2>
        <p>Send us an email and we will action it:</p>
        <div style={styles.highlight}>
          <strong>Email:</strong>{' '}
          <a href="mailto:teslaonlinesolution@gmail.com?subject=Data%20Deletion%20Request" style={styles.link}>
            teslaonlinesolution@gmail.com
          </a><br />
          <strong>Subject line:</strong> Data Deletion Request
        </div>
        <p>Please send the email from the address registered to your account, and include:</p>
        <ol style={styles.ol}>
          <li style={styles.li}>The email address associated with your Tesla CRM Connect account.</li>
          <li style={styles.li}>Your business name as it appears in the dashboard.</li>
          <li style={styles.li}>
            The WhatsApp Business phone number connected to your account, if you have one connected.
          </li>
          <li style={styles.li}>
            Whether you want <strong>your entire account deleted</strong>, or only <strong>specific customer contacts
            and their chat history</strong> removed.
          </li>
        </ol>
        <p>
          We verify that the request comes from the account owner before deleting anything. This protects you from someone
          else deleting your data.
        </p>

        <h2 style={styles.h2}>What Gets Deleted</h2>
        <p>On a full account deletion request, we permanently remove:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Your account and business profile information.</li>
          <li style={styles.li}>Your stored WhatsApp Business account connection, including the access token, WhatsApp Business Account ID, and phone number ID.</li>
          <li style={styles.li}>All contacts stored against your business.</li>
          <li style={styles.li}>All message content, timestamps, and delivery status records for your conversations.</li>
          <li style={styles.li}>Your templates, broadcasts, drip campaigns, and automated reply rules.</li>
        </ul>
        <p>
          If you request removal of only certain contacts, we delete those contacts together with their full chat history
          and leave the rest of your account intact.
        </p>

        <h2 style={styles.h2}>Timeline</h2>
        <p>
          We acknowledge deletion requests within <strong>7 days</strong> and complete verified deletions within{' '}
          <strong>30 days</strong>. We email you to confirm once the deletion is done.
        </p>

        <h2 style={styles.h2}>Deleting Your Connection Yourself</h2>
        <p>
          If you only want to disconnect your WhatsApp Business account and remove the stored access token, you can do
          this yourself at any time: sign in to Tesla CRM Connect, open the <strong>Connect</strong> page, and choose{' '}
          <strong>Disconnect</strong>. This immediately clears the stored credentials for your business. Note that this
          disconnects the integration but does not by itself erase your stored chat history &mdash; email us as described
          above if you want the message data removed too.
        </p>

        <h2 style={styles.h2}>What We May Retain</h2>
        <p>
          After deletion we may retain a minimal record where the law requires it, such as billing and transaction records
          needed for tax and accounting purposes. These records do not include your customers&rsquo; message content.
          Routine encrypted backups are cycled out on our standard retention schedule.
        </p>

        <h2 style={styles.h2}>Data Held by Meta</h2>
        <p>
          Tesla CRM Connect runs on Meta&rsquo;s WhatsApp Business Platform. Deleting your data from our systems does not
          delete data held independently by Meta or the message history stored on your own WhatsApp Business app or on your
          customers&rsquo; devices. To request deletion of data held by Meta, please contact Meta directly.
        </p>

        <h2 style={styles.h2}>Questions</h2>
        <p>
          For anything relating to deletion or your data more generally, see our{' '}
          <a href="/privacy-policy" style={styles.link}>Privacy Policy</a> or contact us:
        </p>
        <div style={styles.callout}>
          <strong>Tesla Online Solution</strong><br />
          Email: <a href="mailto:teslaonlinesolution@gmail.com" style={styles.link}>teslaonlinesolution@gmail.com</a>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
